<div class="workstations-management">
  <div class="header">
    <h2>Workstation Management</h2>
    <button class="btn btn-primary" (click)="openCreateModal()">➕ Add New Workstation</button>
  </div>

  <div *ngIf="error" class="error-message">{{ error }}</div>
  <div *ngIf="loading" class="loading">Loading...</div>

  <table *ngIf="!loading && workstations.length > 0" class="workstations-table">
    <thead>
      <tr>
        <th>ID</th>
        <th>Name</th>
        <th>Client</th>
        <th>Database</th>
        <th>Expected Version</th>
        <th>Roles</th>
        <th>Status</th>
        <th>Upgrade Token</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let ws of workstations">
        <td>{{ ws.clientWsID }}</td>
        <td>
          {{ ws.name }}
          <span *ngIf="ws.offlineUpgradePending" class="offline-pending-badge"
                [title]="'Offline upgrade to ' + ws.offlineUpgradeTargetVersion + ' pending confirmation'">
            📦 Offline Pending
          </span>
        </td>
        <td>{{ ws.clientName }}</td>
        <td>
          <span *ngIf="ws.databaseName" class="db-badge">{{ ws.databaseName }}</span>
          <span *ngIf="!ws.databaseName" class="text-muted">—</span>
        </td>
        <td>
          <span *ngIf="ws.expectedVersion" class="version-badge">{{ ws.expectedVersion }}</span>
          <span *ngIf="!ws.expectedVersion" class="text-muted">—</span>
          <span *ngIf="ws.isOutdated && !ws.offlineUpgradePending" class="outdated-badge"
                [title]="'Latest available: ' + ws.latestAvailableVersion">
            ⚠ Outdated
          </span>
          <span *ngIf="ws.offlineUpgradePending" class="offline-target-badge"
                [title]="'Package generated: ' + (ws.offlinePackageGeneratedAt | date:'dd/MM/yyyy HH:mm')">
            → {{ ws.offlineUpgradeTargetVersion }}
          </span>
        </td>
        <td>
          <span *ngFor="let role of ws.roles" class="role-badge">{{ role }}</span>
          <button class="btn btn-sm" (click)="openRoleModal(ws)" title="Manage Roles">🏷️</button>
        </td>
        <td>
          <span class="status-badge" [class.active]="ws.isActive">
            {{ ws.isActive ? 'Active' : 'Inactive' }}
          </span>
        </td>
        <td class="token-cell">
          <ng-container *ngIf="isTokenActive(ws.latestToken); else noToken">
            <span class="token-badge active">Active</span>
            <span class="token-expiry">exp. {{ ws.latestToken!.expiresAt | date:'HH:mm dd/MM' }}</span>
            <button class="btn btn-sm btn-copy" (click)="copyToken(ws.latestToken!.token)" title="Copy token">Copy</button>
            <button class="btn btn-sm btn-generate" (click)="generateToken(ws)" title="Generate new token">↻</button>
          </ng-container>
          <ng-template #noToken>
            <span *ngIf="ws.latestToken" class="token-badge expired">Expired</span>
            <button class="btn btn-sm btn-generate" (click)="generateToken(ws)">Generate</button>
          </ng-template>
        </td>
        <td class="actions">
          <button class="btn btn-sm" (click)="openLogsModal(ws)" title="Upgrade logs">📋</button>
          <button *ngIf="!ws.offlineUpgradePending && ws.databaseName"
                  class="btn btn-sm btn-offline"
                  (click)="openOfflineModal(ws)"
                  title="Prepare Offline Upgrade Package">📦</button>
          <button *ngIf="ws.offlineUpgradePending"
                  class="btn btn-sm btn-complete-offline"
                  (click)="openCompleteOfflineModal(ws)"
                  title="Mark offline upgrade as done">✅</button>
          <button class="btn btn-sm" (click)="openEditModal(ws)">✏️</button>
          <button class="btn btn-sm" (click)="deleteWorkstation(ws)">🗑️</button>
        </td>
      </tr>
    </tbody>
  </table>

  <!-- Create / Edit Modal -->
  <div class="modal" *ngIf="showModal" (click)="closeModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>{{ isEditMode ? 'Edit Workstation' : 'Add New Workstation' }}</h3>
        <button class="close-btn" (click)="closeModal()">✖</button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label>Client *</label>
          <select [(ngModel)]="currentWorkstation.clientParamID" required>
            <option *ngFor="let client of clients" [value]="client.clientParamID">
              {{ client.clientName }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label>Name *</label>
          <input type="text" [(ngModel)]="currentWorkstation.name" placeholder="Enter workstation name" required />
        </div>
        <div class="form-group">
          <label>Database</label>
          <select [(ngModel)]="currentWorkstation.databaseName"
                  (ngModelChange)="onDatabaseChange($event)">
            <option [ngValue]="undefined">— Select database —</option>
            <option *ngFor="let db of untrackedDatabases" [value]="db">{{ db }}</option>
          </select>
          <small class="form-hint" *ngIf="untrackedDatabases.length === 0">
            No untracked databases found. Add database mappings first.
          </small>
        </div>
        <div class="form-group">
          <label>Expected Version</label>
          <select [(ngModel)]="currentWorkstation.expectedVersion"
                  [disabled]="!currentWorkstation.databaseName || versionsLoading">
            <option [ngValue]="undefined">— Select version —</option>
            <option *ngFor="let v of availableVersions" [value]="v">{{ v }}</option>
          </select>
          <small class="form-hint" *ngIf="currentWorkstation.databaseName && !versionsLoading && availableVersions.length === 0">
            No uploaded versions found for this database.
          </small>
          <small class="form-hint" *ngIf="versionsLoading">Loading versions…</small>
        </div>
        <div class="form-group">
          <label>Description</label>
          <textarea [(ngModel)]="currentWorkstation.description" placeholder="Enter description" rows="3"></textarea>
        </div>
        <div class="form-group checkbox-group">
          <label>
            <input type="checkbox" [(ngModel)]="currentWorkstation.isActive" />
            Active
          </label>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" (click)="closeModal()">Cancel</button>
        <button class="btn btn-primary" (click)="saveWorkstation()">
          {{ isEditMode ? 'Update' : 'Create' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Upgrade Logs Modal -->
  <div class="modal" *ngIf="showLogsModal" (click)="closeLogsModal()">
    <div class="modal-content modal-wide" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Upgrade Logs — {{ selectedWorkstation?.name }}</h3>
        <button class="close-btn" (click)="closeLogsModal()">✖</button>
      </div>
      <div class="modal-body">
        <div *ngIf="logsLoading" class="loading">Loading logs...</div>
        <div *ngIf="!logsLoading && upgradeLogs.length === 0" class="empty-state">
          No upgrade history for this workstation yet.
        </div>
        <table *ngIf="!logsLoading && upgradeLogs.length > 0" class="logs-table">
          <thead>
            <tr>
              <th>Database</th>
              <th>Version</th>
              <th>Upgraded At</th>
              <th>Machine</th>
              <th>Mode</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let log of upgradeLogs">
              <td>{{ log.dbName }}</td>
              <td><span class="version-badge">{{ log.versionName }}</span></td>
              <td>{{ log.upgradedAt | date:'dd/MM/yyyy HH:mm:ss' }}</td>
              <td>{{ log.machineName || '—' }}</td>
              <td>
                <span *ngIf="log.isOffline" class="offline-log-badge">📦 Offline</span>
                <span *ngIf="!log.isOffline" class="online-log-badge">🌐 Online</span>
              </td>
              <td>{{ log.notes || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="modal-footer">
        <button class="btn btn-primary" (click)="closeLogsModal()">Close</button>
      </div>
    </div>
  </div>

  <!-- Role Management Modal -->
  <div class="modal" *ngIf="showRoleModal" (click)="closeRoleModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Manage Roles - {{ selectedWorkstation?.name }}</h3>
        <button class="close-btn" (click)="closeRoleModal()">✖</button>
      </div>
      <div class="modal-body">
        <div class="role-list">
          <div *ngFor="let roleType of filteredRoleTypes" class="role-item">
            <label>
              <input
                type="checkbox"
                [checked]="hasRole(roleType.roleTypeName)"
                (change)="toggleRole(roleType)"
              />
              {{ roleType.roleTypeName }}
              <span class="role-desc" *ngIf="roleType.description">- {{ roleType.description }}</span>
            </label>
          </div>
          <div *ngIf="filteredRoleTypes.length === 0" class="empty-state">
            No roles defined for database "{{ selectedWorkstation?.databaseName || 'this workstation' }}".
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-primary" (click)="closeRoleModal()">Close</button>
      </div>
    </div>
  </div>

  <!-- Offline Package Modal -->
  <div class="modal" *ngIf="showOfflineModal" (click)="closeOfflineModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>📦 Prepare Offline Upgrade — {{ offlineWorkstation?.name }}</h3>
        <button class="close-btn" (click)="closeOfflineModal()">✖</button>
      </div>
      <div class="modal-body">
        <p class="form-hint">
          Select the target version. A ZIP package will be built containing all SQL scripts and
          software artifacts needed to upgrade from <strong>{{ offlineWorkstation?.expectedVersion || '(none)' }}</strong>
          to the selected version.
        </p>

        <div *ngIf="offlineError" class="error-message">{{ offlineError }}</div>

        <div class="form-group" *ngIf="!offlineVersionsLoading">
          <label>Target Version *</label>
          <select [(ngModel)]="selectedOfflineVersion" [disabled]="offlineVersions.length === 0">
            <option value="">— Select version —</option>
            <option *ngFor="let v of offlineVersions" [value]="v">{{ v }}</option>
          </select>
          <small class="form-hint" *ngIf="offlineVersions.length === 0 && !offlineError">
            No newer uploaded versions available for this workstation.
          </small>
        </div>

        <div *ngIf="offlineVersionsLoading" class="loading">Loading available versions…</div>

        <div class="offline-info" *ngIf="selectedOfflineVersion">
          <p>The package will include:</p>
          <ul>
            <li>SQL upgrade scripts for each version in the path</li>
            <li>Software artifacts for all roles assigned to this workstation</li>
            <li>A <code>manifest.json</code> describing the package contents</li>
            <li>A <code>checksum.txt</code> with an HMAC-SHA256 integrity signature</li>
          </ul>
          <p class="warning-hint">⚠ After download, the workstation will be marked as <strong>Offline Upgrade Pending</strong>.
             Use the ✅ button to confirm completion after the upgrade has been performed on-site.</p>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" (click)="closeOfflineModal()">Cancel</button>
        <button class="btn btn-primary"
                (click)="downloadOfflinePackage()"
                [disabled]="!selectedOfflineVersion || offlinePackageBuilding || offlineVersionsLoading">
          {{ offlinePackageBuilding ? 'Building package…' : '⬇ Download Package' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Complete Offline Upgrade Modal -->
  <div class="modal" *ngIf="showCompleteOfflineModal" (click)="closeCompleteOfflineModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>✅ Confirm Offline Upgrade — {{ completeOfflineWorkstation?.name }}</h3>
        <button class="close-btn" (click)="closeCompleteOfflineModal()">✖</button>
      </div>
      <div class="modal-body">
        <p>Confirm that the offline upgrade to version
          <strong>{{ completeOfflineWorkstation?.offlineUpgradeTargetVersion }}</strong>
          has been performed on this workstation.
        </p>
        <p>This will update the expected version and add an offline entry to the upgrade log.</p>

        <div class="form-group">
          <label>Machine Name <small class="text-muted">(optional)</small></label>
          <input type="text" [(ngModel)]="completeOfflineMachineName" placeholder="e.g. DESKTOP-ABC123" />
        </div>
        <div class="form-group">
          <label>Notes <small class="text-muted">(optional)</small></label>
          <textarea [(ngModel)]="completeOfflineNotes" placeholder="e.g. Performed by technician on 31/03/2026" rows="2"></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" (click)="closeCompleteOfflineModal()">Cancel</button>
        <button class="btn btn-primary"
                (click)="confirmCompleteOfflineUpgrade()"
                [disabled]="completeOfflineLoading">
          {{ completeOfflineLoading ? 'Saving…' : '✅ Confirm Upgrade Done' }}
        </button>
      </div>
    </div>
  </div>
</div>
